import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const TOKEN = "f8b12903aa283e211d6f7c74ed452aa37e23dc43d7449980";
const URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/landingapp-views-webhook`;
const VISITOR_KEY = "ls_visitor_id";

interface ClickRecord {
  t: string;
  x: string;
  h: string;
  y: number;
}

interface ViewResponse {
  view_id?: string;
}

function getVisitorId(): string {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid =
        window.crypto && crypto.randomUUID
          ? crypto.randomUUID()
          : `v${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch {
    return "anon";
  }
}

function getUtm(): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
} {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source"),
      utm_medium: p.get("utm_medium"),
      utm_campaign: p.get("utm_campaign"),
    };
  } catch {
    return { utm_source: null, utm_medium: null, utm_campaign: null };
  }
}

function getDevice(): string {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/Edg\//i.test(ua)) return "Edge";
  if (/OPR\//i.test(ua)) return "Opera";
  if (/Chrome\//i.test(ua)) return "Chrome";
  if (/Firefox\//i.test(ua)) return "Firefox";
  if (/Safari\//i.test(ua)) return "Safari";
  return "Khác";
}

function post(body: Record<string, unknown>): Promise<ViewResponse | null> {
  try {
    return fetch(URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((r) => r.json())
      .catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

function resolveCountry(cb: (country: string | null) => void): void {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 1500);
    fetch("https://ipapi.co/json/", { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        clearTimeout(timer);
        cb(d && d.country_code ? String(d.country_code).toUpperCase() : null);
      })
      .catch(() => {
        clearTimeout(timer);
        cb(null);
      });
  } catch {
    cb(null);
  }
}

function start(viewId: string): () => void {
  const startAt = Date.now();
  let maxDepth = 0;
  const clicks: ClickRecord[] = [];

  const computeDepth = (): number => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 100;
    const depth = Math.round(
      ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100,
    );
    return Math.min(Math.max(depth, 0), 100);
  };

  const flush = (): void => {
    const elapsed = Math.round((Date.now() - startAt) / 1000);
    const depth = Math.max(maxDepth, computeDepth());
    const payload = clicks.splice(0);
    post({
      action: "update",
      view_id: viewId,
      visitor_id: getVisitorId(),
      scroll_depth: depth,
      time_on_page: elapsed,
      clicks: payload,
    });
  };

  const onScroll = (): void => {
    const d = computeDepth();
    if (d > maxDepth) maxDepth = d;
  };

  const onClick = (e: MouseEvent): void => {
    if (clicks.length >= 40) return;
    const t = e.target as HTMLElement | null;
    if (!t || !t.closest) return;
    const el =
      (t.closest(
        'a,button,[role="button"],input,select,textarea',
      ) as HTMLElement) || t;
    const tag = (el.tagName || "unknown").toLowerCase();
    const text = String(el.innerText || el.getAttribute("aria-label") || "")
      .trim()
      .slice(0, 40);
    const href = String(el.getAttribute("href") || "").slice(0, 80);
    clicks.push({ t: tag, x: text, h: href, y: Math.round(e.clientY) });
  };

  const onVisibility = (): void => {
    if (document.visibilityState === "hidden") flush();
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", flush);
  const interval = window.setInterval(flush, 60000);

  return () => {
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", flush);
    window.clearInterval(interval);
    flush();
  };
}

export default function ViewTracking() {
  const location = useLocation();

  useEffect(() => {
    const utm = getUtm();
    const visitorId = getVisitorId();
    let cleanup: (() => void) | null = null;
    let active = true;

    resolveCountry((country) => {
      if (!active) return;
      post({
        webhook_token: TOKEN,
        visitor_id: visitorId,
        page_url: window.location.href,
        referrer: document.referrer || null,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        device_type: getDevice(),
        browser: getBrowser(),
        country,
      }).then((res) => {
        if (active && res && res.view_id) cleanup = start(res.view_id);
      });
    });

    return () => {
      active = false;
      if (cleanup) cleanup();
    };
  }, [location.pathname, location.search]);

  return null;
}